import React, { useEffect, useRef, useCallback } from 'react';
import { CSSTransition } from 'react-transition-group';

export default function Options(props) {
  const initialFieldValues = [
    {
      label: 'Brightness',
      type: 'range',
      min: 0,
      max: 200,
      default: 100,
      step: 5,
    },
    {
      label: 'Contrast',
      type: 'range',
      min: 0,
      max: 200,
      default: 100,
      step: 5,
    },
    {
      label: 'Saturation',
      type: 'range',
      min: 0,
      max: 200,
      default: 100,
      step: 5,
    },
    {
      label: 'Hue',
      type: 'range',
      min: -180,
      max: 180,
      default: 0,
      step: 5,
    },
    {
      label: 'Rotation',
      type: 'range',
      min: 0,
      max: 270,
      default: 0,
      step: 90,
    },
    {
      label: 'Grayscale',
      type: 'checkbox',
      checked: false,
    },
    {
      label: 'Invert',
      type: 'checkbox',
      checked: false,
    },
  ];

  const {
    osdInstance,
    images,
    setIsOptionsOpen,
    isOptionsOpen,
    optionsButtonRef,
    imageManipulations,
    setImageManipulations,
    activeImageIndex,
    setActiveImageIndex,
  } = props;

  const nodeRef = useRef();

  const handleManipulationChange = (e, label) => {
    setImageManipulations((prevState) => {
      const newValue = {};
      if (e.target.type === 'range') {
        newValue[label] = e.target.value;
      } else if (e.target.type === 'checkbox') {
        newValue[label] = e.target.checked;
      }

      return {
        ...prevState,
        ...newValue,
      };
    });
  };

  const renderImageOptions = () => {
    return images.map((image, i) => {
      return (
        <option key={i} value={i}>
          {image.label}
        </option>
      );
    });
  };

  const renderImageManipulationOptions = () => {
    return initialFieldValues.map((field, index) => {
      let input;

      if (field.type === 'range') {
        input = (
          <input
            id={`viewer-option-input-${field.label.toLowerCase()}`}
            className="viewer-option__input"
            aria-labelledby={`viewer-option-label-${field.label.toLowerCase()}`}
            type="range"
            min={field.min}
            max={field.max}
            step={field.step}
            value={imageManipulations[field.label]}
            onChange={(e) => {
              handleManipulationChange(e, field.label);
            }}
            // Required for custom styling
            style={`--percent:${
              (imageManipulations[field.label] - field.min) /
              (field.max - field.min)
            }`}
          />
        );
      }

      if (field.type === 'checkbox') {
        input = (
          <input
            id={`viewer-option-input-${field.label.toLowerCase()}`}
            className="viewer-option__input"
            aria-labelledby={`viewer-option-label-${field.label.toLowerCase()}`}
            type="checkbox"
            checked={imageManipulations[field.label]}
            onChange={(e) => {
              handleManipulationChange(e, field.label);
            }}
          />
        );
      }

      return (
        <div key={index} className="viewer-option">
          <label
            id={`viewer-option-label-${field.label.toLowerCase()}`}
            className="viewer-option__label"
            htmlFor={`viewer-option-input-${field.label.toLowerCase()}`}
          >
            {field.label}
          </label>
          <div className="viewer-option__control">
            {input}
            <div className="viewer-option__value">
              {imageManipulations[field.label]}
            </div>
          </div>
        </div>
      );
    });
  };

  // Returns an object that can be used by the imageManuipulations Hook
  const resetImageManipulations = () => {
    let newFieldValues = {};
    initialFieldValues.forEach((field) => {
      const newField = {};

      if (field.type === 'range') {
        newField[field.label] = field.default;
      } else if (field.type === 'checkbox') {
        newField[field.label] = field.checked;
      }
      newFieldValues = { ...newFieldValues, ...newField };
    });

    // Add this in here to save having two buttons
    osdInstance.viewport.goHome();

    return newFieldValues;
  };

  const handleClose = () => {
    setIsOptionsOpen(false);
    // Return focus to the toolbar button when closing this way
    optionsButtonRef.current.focus();
  };

  // Compare state to osdInstance.viewport rotation, then update the state
  const rotateCallback = useCallback(() => {
    setTimeout(() => {
      setImageManipulations((prevState) => {
        return {
          ...prevState,
          ...{ Rotation: osdInstance.viewport.getRotation() },
        };
      });
    }, 0);
  }, [osdInstance, imageManipulations]);

  // Compare state to osdInstance.currentPage, then update the state
  const pageCallback = useCallback(() => {
    setActiveImageIndex(osdInstance.currentPage());
  }, [osdInstance, activeImageIndex]);

  // Translate image manipulation states into meaningful outputs
  useEffect(() => {
    const canvas = osdInstance.element.querySelector('canvas');

    // Set rotation against OSD
    if (imageManipulations.Rotation !== osdInstance.viewport.getRotation()) {
      osdInstance.viewport.setRotation(imageManipulations['Rotation']);
    }
    // Set inline style for each of the CSS based manipulations
    canvas.style.filter = `brightness(${imageManipulations['Brightness']}%)`;
    canvas.style.filter += `contrast(${imageManipulations['Contrast']}%)`;
    canvas.style.filter += `saturate(${imageManipulations['Saturation']}%)`;
    canvas.style.filter += `hue-rotate(${imageManipulations['Hue']}deg)`;
    canvas.style.filter += `grayscale(${
      imageManipulations['Grayscale'] ? 1 : 0
    })`;
    canvas.style.filter += `invert(${imageManipulations['Invert'] ? 1 : 0})`;
  }, [osdInstance, activeImageIndex, imageManipulations]);

  // Change page when image index updates
  useEffect(() => {
    osdInstance.goToPage(activeImageIndex);
  }, [osdInstance, activeImageIndex]);

  // Attach events handlers when osdInstance changes
  // These can fire via Options sliders or via Keyboard input
  useEffect(() => {
    if (osdInstance) {
      osdInstance.removeAllHandlers('rotate');
      osdInstance.addHandler('rotate', rotateCallback);
    }
  }, [osdInstance]);

  useEffect(() => {
    if (osdInstance) {
      osdInstance.removeAllHandlers('page');
      osdInstance.addHandler('page', pageCallback);
    }
  }, [osdInstance]);

  return (
    <CSSTransition
      nodeRef={nodeRef}
      in={isOptionsOpen}
      classNames="viewer-options"
      timeout={300}
    >
      <div ref={nodeRef} id="viewer-options" className="viewer-options">
        <div className="viewer-options__inner">
          <button
            className="button button--icon-only viewer-options__close"
            onClick={handleClose}
            aria-controls="viewer-options"
            aria-expanded={isOptionsOpen}
          >
            <span className="button__wrap">
              <span className="visually-hidden button__text">Close</span>
              <svg className="button__icon">
                <use href="/assets/images/icons.svg#close"></use>
              </svg>
            </span>
          </button>
          <div className="viewer-option-section">
            <h2 className="viewer-options-heading h3">Image selection</h2>
            <div className="viewer-option-grid">
              <div className="viewer-option">
                <label
                  id="viewer-option-label-image"
                  className="viewer-option__label"
                  htmlFor="viewer-option-input-image"
                >
                  Image
                </label>
                <div className="viewer-option__control">
                  <select
                    id="viewer-option-input-image"
                    className="viewer-option__input"
                    aria-labelledby="viewer-option-label-image"
                    onChange={(e) => setActiveImageIndex(e.target.value)}
                    value={activeImageIndex}
                  >
                    {renderImageOptions()}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="viewer-option-section">
            <h2 className="viewer-options-heading h3">Image adjustments</h2>
            <div className="viewer-option-grid">
              {renderImageManipulationOptions()}
            </div>
          </div>
          <button
            className="button button--standard viewer-options__reset"
            type="button"
            onClick={() => setImageManipulations(resetImageManipulations())}
          >
            <span className="button__wrap">
              <span className="button__text">Reset</span>
              <svg className="button__icon">
                <use href="/assets/images/icons.svg#refresh"></use>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </CSSTransition>
  );
}
